import React from 'react';
import { Box, IconButton, Paper, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import dayjs from 'dayjs';
import type { PulseCohortRow } from '../../types/contactOptimization';
import ChartSectionHeader from './ChartSectionHeader';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import { SectionScopeChip } from './ScopeBanner';

interface DayBucket {
  day: string;
  totalLeads: number;
  neverContacted: number;
  byOffset: Record<number, { leads: number; contacted: number }>;
}

function buildBuckets(rows: PulseCohortRow[]): DayBucket[] {
  const byDay = new Map<string, DayBucket>();
  for (const row of rows) {
    const day = row.consumption_day ?? 'unknown';
    const bucket = byDay.get(day) ?? {
      day,
      totalLeads: 0,
      neverContacted: 0,
      byOffset: {},
    };
    if (row.day_offset === null || row.day_offset === undefined) {
      bucket.neverContacted += row.never_contacted;
      bucket.totalLeads += row.leads;
    } else {
      bucket.byOffset[row.day_offset] = {
        leads: (bucket.byOffset[row.day_offset]?.leads ?? 0) + row.leads,
        contacted: (bucket.byOffset[row.day_offset]?.contacted ?? 0) + row.contacted,
      };
      bucket.totalLeads += row.leads;
    }
    byDay.set(day, bucket);
  }
  return Array.from(byDay.values()).sort((a, b) => (a.day < b.day ? 1 : -1));
}

function dayLabel(day: string): string {
  return dayjs(day).isValid() ? dayjs(day).format('MMM D') : day;
}

function exportCohortCsv(buckets: DayBucket[], offsets: number[]) {
  const header = [
    'consumption_day',
    'leads',
    ...offsets.map((o) => `pct_connected_by_day_${o}`),
    'dialed_not_yet_connected',
    'dialed_not_yet_connected_pct',
    'never_reached',
    'never_reached_pct',
  ];
  const lines = [header.join(',')];
  for (const bucket of buckets) {
    let cumulativeContacted = 0;
    let totalDialed = 0;
    const pctCols = offsets.map((o) => {
      const cell = bucket.byOffset[o];
      cumulativeContacted += cell?.contacted ?? 0;
      totalDialed += cell?.leads ?? 0;
      return bucket.totalLeads > 0 ? ((100 * cumulativeContacted) / bucket.totalLeads).toFixed(1) : '0';
    });
    const dialedNoAnswer = Math.max(0, totalDialed - cumulativeContacted);
    const dialedNoAnswerPct = bucket.totalLeads > 0 ? (100 * dialedNoAnswer) / bucket.totalLeads : 0;
    const neverPct = bucket.totalLeads > 0 ? (100 * bucket.neverContacted) / bucket.totalLeads : 0;
    lines.push(
      [
        bucket.day,
        bucket.totalLeads,
        ...pctCols,
        dialedNoAnswer,
        dialedNoAnswerPct.toFixed(1),
        bucket.neverContacted,
        neverPct.toFixed(1),
      ].join(','),
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'contact-speed-by-consumption-day.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * "Who and when" -- brought back from the earlier dialing_opportunity
 * build's own CohortGrid, rebuilt on the shared call_facts foundation
 * (see queries.py::pulse_cohort_sql) so "connect" here means the same
 * 90-second threshold as everywhere else on this page. Answers a
 * different question than the rest of Pulse: not "how well did this
 * week's calling perform," but "how fast do brand-new leads actually get
 * their first connect, once they come in."
 */
export default function ContactSpeedCohort({ rows }: { rows: PulseCohortRow[] }) {
  const theme = useTheme();
  const [showHelp, setShowHelp] = React.useState(false);

  const buckets = React.useMemo(() => buildBuckets(rows), [rows]);
  const maxOffset = React.useMemo(() => {
    let max = 0;
    for (const b of buckets) {
      for (const key of Object.keys(b.byOffset)) {
        max = Math.max(max, Number(key));
      }
    }
    return max;
  }, [buckets]);
  const offsets = React.useMemo(() => Array.from({ length: maxOffset + 1 }, (_, i) => i), [maxOffset]);

  const cellColor = (pct: number) => alpha(theme.palette.info.main, 0.06 + Math.min(1, pct / 100) * 0.7);
  const dayColWidth = 96;
  const cellMinWidth = 40;

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        p: 2,
        mb: 2.5,
        border: `1px solid ${t.tokens.border.subtle}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <ChartSectionHeader
        title="Contact speed by consumption day"
        subtitle="Each row is a day leads came in, over the last 7 days — tracked for up to 15 days after each lead's own arrival, unlike the fixed last-7-days-vs-baseline comparison every section above uses."
        actions={
          <>
            <SectionScopeChip window={{ label: 'Last 7 days', detail: 'New leads only, tracked up to 15 days after \u2014 not a last-7-days-vs-baseline comparison' }} />
            <Tooltip title="Download this table as a CSV file" arrow>
              <IconButton
                size="small"
                onClick={() => exportCohortCsv(buckets, offsets)}
                aria-label="Download contact speed table as CSV"
                disabled={!buckets.length}
              >
                <DownloadRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <ExplainerToggle title="How to read this table" open={showHelp} onClick={() => setShowHelp((v) => !v)} />
          </>
        }
      />
      <ExplainerNote title="How to read this table" open={showHelp}>
        {`Each row is a **consumption day**, a day new leads came in. The **+Nd** columns show what share of that day's leads had their **first** outbound call connect (90+ seconds) by that many days after they came in. Each column is cumulative, so +3d includes everyone already counted in +0d through +2d.

Every lead in a row ends up in exactly one of three buckets, and the three always add up to the full row:

- Connected by some **+Nd** column (shown as that column's %).
- **Dialed, not yet connected** — we called at least once within their 15-day reachability window, but the first call never connected.
- **Never** — no call yet within a 15-day reachability window from when they came in. For very recent rows this mostly means "we haven't gotten to them yet, and there's still time," not a final verdict.

Only counts leads that qualify as MQLs (marketing-qualified leads — leads that meet the basic bar to be worth calling at all), matching the population used on the Queue tab. "Connect" is the same 90+ second definition used everywhere else on this page.`}
      </ExplainerNote>

      {buckets.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No qualifying leads in this window yet.
        </Typography>
      ) : (
        <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
          <Box sx={{ minWidth: dayColWidth + (offsets.length + 2) * cellMinWidth }}>
            <Stack direction="row" sx={{ mb: 0.5 }}>
              <Box sx={{ width: dayColWidth, flexShrink: 0 }} />
              {offsets.map((o) => (
                <Box key={o} sx={{ flex: 1, minWidth: cellMinWidth, textAlign: 'center' }}>
                  <Tooltip
                    title={o === 0 ? 'Connected the same day the lead came in' : `Connected within ${o} day${o === 1 ? '' : 's'} of the lead coming in`}
                    arrow
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>
                      {`+${o}d`}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
              <Box sx={{ flex: 1.3, minWidth: cellMinWidth + 20, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Dialed, not yet connected
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: cellMinWidth, textAlign: 'center' }}>
                <Typography variant="caption" color="error" sx={{ fontWeight: 700 }}>
                  Never
                </Typography>
              </Box>
            </Stack>

            {buckets.map((bucket) => {
              let cumulativeContacted = 0;
              let totalDialed = 0;
              return (
                <Stack key={bucket.day} direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                  <Box sx={{ width: dayColWidth, flexShrink: 0, pr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {dayLabel(bucket.day)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`${bucket.totalLeads.toLocaleString()} leads`}
                    </Typography>
                  </Box>
                  {offsets.map((o) => {
                    const cell = bucket.byOffset[o];
                    cumulativeContacted += cell?.contacted ?? 0;
                    totalDialed += cell?.leads ?? 0;
                    const pct = bucket.totalLeads > 0 ? (100 * cumulativeContacted) / bucket.totalLeads : 0;
                    const tooltip = `${dayLabel(bucket.day)} \u00b7 +${o}d\nReached by this day: ${cumulativeContacted.toLocaleString()} of ${bucket.totalLeads.toLocaleString()} (${pct.toFixed(0)}%)`;
                    return (
                      <Tooltip
                        key={o}
                        title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: cellMinWidth,
                            height: 30,
                            mx: '1px',
                            borderRadius: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: cellColor(pct),
                            cursor: 'default',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>
                            {pct > 0 ? `${pct.toFixed(0)}%` : ''}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {(() => {
                    const dialedNoAnswer = Math.max(0, totalDialed - cumulativeContacted);
                    const dialedNoAnswerPct = bucket.totalLeads > 0 ? (100 * dialedNoAnswer) / bucket.totalLeads : 0;
                    return (
                      <Tooltip
                        title={`${dialedNoAnswer.toLocaleString()} of ${bucket.totalLeads.toLocaleString()} were dialed but their first call never connected for 90+ seconds (no answer, voicemail, short call, etc.)`}
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            flex: 1.3,
                            minWidth: cellMinWidth + 20,
                            height: 30,
                            mx: '1px',
                            borderRadius: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: dialedNoAnswer
                              ? alpha(theme.palette.warning.main, 0.14)
                              : alpha(theme.palette.text.disabled, 0.06),
                            cursor: 'default',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: 'warning.dark' }}>
                            {dialedNoAnswer
                              ? `${dialedNoAnswer.toLocaleString()} (${dialedNoAnswerPct.toFixed(0)}%)`
                              : ''}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  <Tooltip
                    title={`${bucket.neverContacted.toLocaleString()} of ${bucket.totalLeads.toLocaleString()} not yet reached within their 15-day window`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: cellMinWidth,
                        height: 30,
                        mx: '1px',
                        borderRadius: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: bucket.neverContacted
                          ? alpha(theme.palette.error.main, 0.12)
                          : alpha(theme.palette.text.disabled, 0.06),
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: 'error.main' }}>
                        {bucket.neverContacted || ''}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Stack>
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
}
